import React, { useEffect, useState, useCallback } from "react";
import {
  Paper,
  makeStyles,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), margin: theme.spacing(2) },
  filters: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap", marginBottom: theme.spacing(2) },
  field: { minWidth: 160 },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: theme.spacing(2) }
}));

const emptyForm = {
  name: "",
  sku: "",
  description: "",
  categoryId: "",
  type: "product",
  unit: "un",
  price: 0,
  cost: 0,
  stockControl: false,
  stockQty: 0,
  active: true
};

const Products = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ searchParam: "", categoryId: "", active: "" });
  const [dialog, setDialog] = useState({ open: false, form: emptyForm, editing: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageNumber: page, pageSize };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      const { data } = await api.get("/products", { params });
      setRows(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (e) {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    api.get("/product-categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => setDialog({ open: true, form: emptyForm, editing: null });
  const openEdit = (p) => setDialog({ open: true, form: { ...emptyForm, ...p, categoryId: p.categoryId ?? "" }, editing: p });
  const close = () => setDialog((d) => ({ ...d, open: false }));

  const save = async () => {
    try {
      const payload = { ...dialog.form };
      if (payload.categoryId === "") payload.categoryId = null;
      if (dialog.editing) {
        await api.put(`/products/${dialog.editing.id}`, payload);
        toast.success("Atualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Criado");
      }
      close();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
    }
  };

  const remove = async (p) => {
    if (!(await confirm({ title: `Excluir o produto "${p.name}"?`, message: "Propostas e negócios já criados mantêm os itens; só o catálogo perde o produto." }))) return;
    try {
      await api.delete(`/products/${p.id}`);
      fetchData();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const f = dialog.form;
  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  return (
    <Paper className={classes.root}>
      {confirmDialog}
      <Typography variant="h5" gutterBottom>Produtos & Serviços</Typography>
      <div className={classes.filters}>
        <TextField label="Buscar" className={classes.field} value={filters.searchParam}
          onChange={(e) => setFilters((f) => ({ ...f, searchParam: e.target.value }))} />
        <TextField select label="Categoria" className={classes.field} value={filters.categoryId}
          onChange={(e) => setFilters((f) => ({ ...f, categoryId: e.target.value }))}>
          <MenuItem value="">Todas</MenuItem>
          {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <TextField select label="Ativo" className={classes.field} value={filters.active}
          onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="true">Sim</MenuItem>
          <MenuItem value="false">Não</MenuItem>
        </TextField>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>Novo</Button>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nome</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell align="right">Preço</TableCell>
            <TableCell align="right">Estoque</TableCell>
            <TableCell>Ativo</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.sku || "—"}</TableCell>
              <TableCell>{r.category?.name || "—"}</TableCell>
              <TableCell>{r.type}</TableCell>
              <TableCell align="right">{Number(r.price).toFixed(2)}</TableCell>
              <TableCell align="right">{r.stockControl ? Number(r.stockQty) : "—"}</TableCell>
              <TableCell><Chip size="small" label={r.active ? "Sim" : "Não"} color={r.active ? "primary" : "default"} /></TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center">Nenhum produto.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <div className={classes.pagination}>
        <Typography variant="body2">Total: {total}</Typography>
        <div>
          <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span style={{ margin: "0 8px" }}>{page} / {totalPages}</span>
          <Button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
        </div>
      </div>

      <Dialog open={dialog.open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editing ? "Editar produto" : "Novo produto"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField label="Nome *" fullWidth value={f.name} onChange={(e) => setF({ name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={3}><TextField label="SKU" fullWidth value={f.sku} onChange={(e) => setF({ sku: e.target.value })} /></Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Tipo" fullWidth value={f.type} onChange={(e) => setF({ type: e.target.value })}>
                <MenuItem value="product">Produto</MenuItem>
                <MenuItem value="service">Serviço</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Categoria" fullWidth value={f.categoryId} onChange={(e) => setF({ categoryId: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}><TextField label="Unidade" fullWidth value={f.unit} onChange={(e) => setF({ unit: e.target.value })} /></Grid>
            <Grid item xs={12} sm={2}><TextField label="Preço" type="number" fullWidth value={f.price} onChange={(e) => setF({ price: e.target.value })} /></Grid>
            <Grid item xs={12} sm={2}><TextField label="Custo" type="number" fullWidth value={f.cost} onChange={(e) => setF({ cost: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField label="Descrição" fullWidth multiline rows={2} value={f.description} onChange={(e) => setF({ description: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><FormControlLabel control={<Checkbox checked={!!f.stockControl} onChange={(e) => setF({ stockControl: e.target.checked })} />} label="Controla estoque" /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Qtd. estoque" type="number" fullWidth value={f.stockQty} onChange={(e) => setF({ stockQty: e.target.value })} disabled={!f.stockControl} /></Grid>
            <Grid item xs={12} sm={4}><FormControlLabel control={<Checkbox checked={!!f.active} onChange={(e) => setF({ active: e.target.checked })} />} label="Ativo" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Products;
